import { Routes } from '@angular/router';
import { authGuard } from './auth/guards/auth.guard';
import { adminGuard } from './auth/guards/admin.guard';
import { setPasswordGuard } from './auth/guards/set-password.guard';
import { guestGuard } from './auth/guards/guest.guard';
import { termsGuard } from './auth/guards/terms.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { licenseFeatureGuard } from './core/guards/license-feature.guard';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () => import('./auth/login-page/login-page').then((m) => m.LoginPage),
    title: 'Mapa Výuky - Přihlášení',
  },
  {
    path: 'forgot-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./auth/forgot-password-page/forgot-password-page').then((m) => m.ForgotPasswordPage),
    title: 'Mapa Výuky - Obnovení hesla',
  },
  {
    path: 'set-password',
    canActivate: [guestGuard, setPasswordGuard],
    loadComponent: () =>
      import('./auth/set-password-page/set-password-page').then((m) => m.SetPasswordPage),
    title: 'Mapa Výuky - Nastavení hesla',
  },
  {
    // No guard: the link is opened wherever the new mailbox is read, usually signed out.
    path: 'confirm-email-change',
    loadComponent: () =>
      import('./auth/confirm-email-change-page/confirm-email-change-page').then(
        (m) => m.ConfirmEmailChangePage,
      ),
    title: 'Mapa Výuky - Potvrzení změny e-mailu',
  },
  {
    path: 'accept-invitation',
    loadComponent: () =>
      import('./auth/accept-invitation-page/accept-invitation-page').then(
        (m) => m.AcceptInvitationPage,
      ),
    title: 'Mapa Výuky - Přijetí pozvánky',
  },
  {
    // No guard: the login form links here, so the text has to be readable before signing in.
    path: 'podminky-uzivani',
    loadComponent: () =>
      import('./terms-of-use/terms-of-use-page/terms-of-use-page').then((m) => m.TermsOfUsePage),
    title: 'Mapa Výuky - Podmínky užívání',
  },
  {
    // Only authGuard: this is where termsGuard sends a signed-in user who has yet to accept.
    path: 'prijeti-podminek',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./terms-of-use/accept-terms-page/accept-terms-page').then((m) => m.AcceptTermsPage),
    title: 'Mapa Výuky - Souhlas s podmínkami užívání',
  },
  {
    path: '',
    canActivate: [authGuard, termsGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./home/home').then((m) => m.Home),
        title: 'Mapa Výuky',
      },
      {
        path: 'prvni-kroky',
        loadComponent: () =>
          import('./getting-started/getting-started-page/getting-started-page').then(
            (m) => m.GettingStartedPage,
          ),
        title: 'Mapa Výuky - První kroky v systému',
      },
      {
        path: 'co-je-noveho',
        loadComponent: () =>
          import('./release-notes/release-notes-page/release-notes-page').then(
            (m) => m.ReleaseNotesPage,
          ),
        title: 'Mapa Výuky - Co je nového',
      },
      {
        path: 'zakladni-skoleni',
        canActivate: [licenseFeatureGuard('basicTraining')],
        loadComponent: () =>
          import('./training/training-page/training-page').then((m) => m.TrainingPage),
        title: 'Mapa Výuky - Základní školení',
      },
      {
        path: 'jak-planovat-vzdelavani-skoly',
        canActivate: [licenseFeatureGuard('advancedTraining')],
        loadComponent: () =>
          import('./advanced-training/advanced-training-page/advanced-training-page').then(
            (m) => m.AdvancedTrainingPage,
          ),
        title: 'Mapa Výuky - Jak plánovat vzdělávání školy',
      },
      {
        path: 'jak-vest-denik-vyuky',
        canActivate: [licenseFeatureGuard('diaryCourse')],
        loadComponent: () =>
          import('./diary-course/diary-course-page/diary-course-page').then(
            (m) => m.DiaryCoursePage,
          ),
        title: 'Mapa Výuky - Jak a proč vést Deník výuky?',
      },
      {
        path: 'ai-asistent',
        loadComponent: () =>
          import('./ai-assistant/ai-assistant-page/ai-assistant-page').then(
            (m) => m.AiAssistantPage,
          ),
        title: 'Mapa Výuky - S čím vám AI pomůže?',
      },
      {
        path: 'jak-na-tematicke-plany',
        loadComponent: () =>
          import('./thematic-plan-guide/thematic-plan-guide-page/thematic-plan-guide-page').then(
            (m) => m.ThematicPlanGuidePage,
          ),
        title: 'Mapa Výuky - Jak na tvorbu tematických plánů?',
      },
      {
        path: 'jak-na-analyzu-ovu',
        loadComponent: () =>
          import('./ovu-analysis-guide/ovu-analysis-guide-page/ovu-analysis-guide-page').then(
            (m) => m.OvuAnalysisGuidePage,
          ),
        title: 'Mapa Výuky - Jak postupovat při analýze OVU',
      },
      {
        path: 'import-ze-smile',
        loadComponent: () =>
          import('./smile-import-guide/smile-import-guide-page/smile-import-guide-page').then(
            (m) => m.SmileImportGuidePage,
          ),
        title: 'Mapa Výuky - Import ŠVP ze SMILE',
      },
      {
        path: 'schools',
        loadComponent: () => import('./schools/school-page/school-page').then((m) => m.SchoolPage),
        title: 'Mapa Výuky - Školy',
      },
      {
        path: 'schools/:schoolId/profile',
        loadComponent: () =>
          import('./schools/school-profile-page/school-profile-page-loader').then(
            (m) => m.SchoolProfilePageLoader,
          ),
        canDeactivate: [unsavedChangesGuard],
        title: 'Mapa Výuky - Profil školy',
      },
      {
        path: 'schools/:schoolId/licenses',
        loadComponent: () =>
          import('./school-license/school-license-page/school-license-page').then(
            (m) => m.SchoolLicensePage,
          ),
        title: 'Mapa Výuky - Licence a AI kredit',
      },
      {
        path: 'schools/:schoolId/ai-settings',
        loadComponent: () =>
          import('./school-ai-settings/school-ai-settings-page/school-ai-settings-page-loader').then(
            (m) => m.SchoolAiSettingsPageLoader,
          ),
        canDeactivate: [unsavedChangesGuard],
        title: 'Mapa Výuky - Nastavení AI',
      },
      {
        path: 'schools/:schoolId/users',
        loadComponent: () =>
          import('./school-users/school-users-page/school-users-page').then(
            (m) => m.SchoolUsersPage,
          ),
        title: 'Mapa Výuky - Uživatelé školy',
      },
      {
        path: 'users',
        canActivate: [adminGuard],
        loadComponent: () => import('./users/user-page/user-page').then((m) => m.UserPage),
        title: 'Mapa Výuky - Správa uživatelů',
      },
      {
        path: 'rvp-versions',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./rvp-version/rvp-version-page/rvp-version-page').then((m) => m.RvpVersionPage),
        title: 'Mapa Výuky - RVP Verze',
      },
      {
        path: 'svps',
        loadComponent: () => import('./svp/svp-page/svp-page').then((m) => m.SvpPage),
        title: 'Mapa Výuky - ŠVP',
      },
      {
        path: 'svps/:svpId',
        loadComponent: () =>
          import('./svp-detail/svp-detail-page-loader/svp-detail-page-loader').then(
            (m) => m.SvpDetailPageLoader,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./svp-overview/svp-overview-page-loader/svp-overview-page-loader').then(
                (m) => m.SvpOverviewPageLoader,
              ),
            title: 'Mapa Výuky - Přehled',
          },
          {
            path: 'subjects',
            loadComponent: () =>
              import('./subjects/subject-page/subject-page').then((m) => m.SubjectPage),
            title: 'Mapa Výuky - Předměty',
          },
          {
            path: 'subjects/:subjectId/profile',
            loadComponent: () =>
              import('./subject-profile/subject-profile-page/subject-profile-page-loader').then(
                (m) => m.SubjectProfilePageLoader,
              ),
            canDeactivate: [unsavedChangesGuard],
            title: 'Mapa Výuky - Profil předmětu',
          },
          {
            path: 'study-program',
            loadComponent: () =>
              import('./study-program/study-program-page/study-program-page').then(
                (m) => m.StudyProgramPage,
              ),
            canDeactivate: [unsavedChangesGuard],
            title: 'Mapa Výuky - Učební Plán',
          },
          {
            path: 'thematic-programs',
            loadComponent: () =>
              import('./thematic-programs/thematic-program-page/thematic-program-page.component').then(
                (m) => m.ThematicProgramPage,
              ),
            title: 'Mapa Výuky - Tematické Plány',
          },
          {
            path: 'ovu-relationships',
            loadComponent: () =>
              import('./ovu-relationships/ovu-relationships-page/ovu-relationships-page').then(
                (m) => m.OvuRelationshipsPage,
              ),
            title: 'Mapa Výuky - Vazby ŠOVU a OVU',
          },
          {
            path: 'ovu-analysis',
            loadComponent: () =>
              import('./ovu-analysis/ovu-analysis-page/ovu-analysis-page').then(
                (m) => m.OvuAnalysisPage,
              ),
            title: 'Mapa Výuky - Analýza OVU',
          },
          {
            path: 'thematic-programs/:subjectId/:gradeId',
            loadComponent: () =>
              import('./thematic-blocks/thematic-blocks-page/thematic-blocks-page.component').then(
                (m) => m.ThematicBlocksPage,
              ),
            title: 'Mapa Výuky - Tematické Bloky',
          },
        ],
      },
      {
        path: 'svps/:svpId/profile',
        loadComponent: () =>
          import('./svp-profile/svp-profile-page/svp-profile-page-loader').then(
            (m) => m.SvpProfilePageLoader,
          ),
        canDeactivate: [unsavedChangesGuard],
        title: 'Mapa Výuky - Profil ŠVP',
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
